import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Cozy Drum songbook pop-up (snippets/pd-carnet-popup.liquid).
 *
 * The snippet renders one template per offer, each listing the drums it is for (data-drum-ids): the paper
 * songbook for the Mini, the Christmas PDF songbook for the Plus and the Pro.
 * When a tongue drum is added from a product form, the cart drawer's auto-open is held back for that add
 * (its `auto-open` attribute is removed while the event is dispatched), the pop-up of that drum shows the 10-minute
 * offer, and the drawer opens once the pop-up closes. Accepting adds the songbook and applies the offer code;
 * when the timer ends the offer is withdrawn. The pop-up shows once per visit (session storage).
 */

const SEEN_KEY = 'pd-carnet-popup-seen';
let shown = false;

function hasBeenSeen() {
  try {
    return sessionStorage.getItem(SEEN_KEY) !== null;
  } catch {
    return false;
  }
}

function markSeen() {
  try {
    sessionStorage.setItem(SEEN_KEY, String(Date.now()));
  } catch {
    // Storage blocked: the `shown` flag still limits the pop-up to once per page.
  }
}

/** @param {string} productId */
function getTemplate(productId) {
  const templates = Array.from(document.querySelectorAll('template[data-pd-carnet-popup]'));
  const template = templates.find((element) => (element instanceof HTMLElement ? element.dataset.drumIds || '' : '').split(',').includes(productId));
  return template instanceof HTMLTemplateElement ? template : null;
}

function openCartDrawer() {
  const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
  if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
}

/** @param {number} seconds */
function formatClock(seconds) {
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * Adds the songbook, applies the offer code (keeping the codes already on the cart) and resolves the cart
 * event with the re-rendered cart sections, as product-form.js does after an add.
 * @param {HTMLTemplateElement} template
 * @param {HTMLButtonElement} button
 */
async function addSongbook(template, button) {
  const { variantId = '', code = '' } = template.dataset;
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const deferredEventPromise = CartLinesUpdateEvent.createPromise();

  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferredEventPromise.promise,
    })
  );

  try {
    const added = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    }).then((response) => response.json());
    if (added.status) throw new Error(added.description || added.message || 'Add to cart failed');

    const current = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then(
      (response) => response.json()
    );
    const codes = (current.discount_codes || [])
      .map((discount) => discount.code)
      .filter((existing) => existing && existing.toUpperCase() !== code.toUpperCase());
    if (code) codes.push(code);

    const cart = await fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ discount: codes.join(','), sections: sectionIds }),
    }).then((response) => response.json());

    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: 1,
        sections: cart.sections,
        source: 'pd-carnet-popup',
        didError: false,
      },
    });
  } catch (error) {
    console.warn('[pd-carnet-popup] Add failed:', error);
    // Let the cart re-render itself from the server state, whatever part of the add went through.
    try {
      const cart = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then(
        (response) => response.json()
      );
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'pd-carnet-popup', didError: true },
      });
    } catch (refreshError) {
      deferredEventPromise.reject(refreshError);
    }
  }
}

/** @param {HTMLTemplateElement} template */
function showPopup(template) {
  const dialog = template.content.firstElementChild?.cloneNode(true);
  if (!(dialog instanceof HTMLDialogElement)) return openCartDrawer();

  shown = true;
  markSeen();
  document.body.append(dialog);

  const clock = dialog.querySelector('[data-pd-popup-clock]');
  const label = dialog.querySelector('[data-pd-popup-label]');
  const addButton = dialog.querySelector('[data-pd-popup-add]');
  const skipButton = dialog.querySelector('[data-pd-popup-skip-main]');
  const endsAt = Date.now() + (Number(template.dataset.seconds) || 600) * 1000;
  let expired = false;

  const tick = () => {
    const secondsLeft = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
    if (clock) clock.textContent = formatClock(secondsLeft);
    if (secondsLeft > 0 || expired) return;

    expired = true;
    clearInterval(timer);
    dialog.classList.add('pd-carnet-popup--expired');
    if (label) label.textContent = "L'offre est terminée.";
    if (addButton instanceof HTMLButtonElement) addButton.disabled = true;
    if (skipButton) skipButton.textContent = 'Continuer';
  };
  const timer = setInterval(tick, 1000);
  tick();

  dialog.addEventListener(
    'close',
    () => {
      clearInterval(timer);
      dialog.remove();
      openCartDrawer();
    },
    { once: true }
  );

  dialog.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // A click on the dimmed backdrop targets the dialog itself.
    if (target === dialog || target.closest('[data-pd-popup-skip]')) {
      dialog.close();
      return;
    }

    const button = target.closest('[data-pd-popup-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled || expired) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    await addSongbook(template, button);
    dialog.close();
  });

  dialog.showModal();
}

window.addEventListener(
  StandardEvents.cartLinesUpdate,
  (event) => {
    const cartEvent = /** @type {CartLinesUpdateEvent} */ (event);
    if (cartEvent.action !== 'add' || shown || hasBeenSeen()) return;

    const form = event.target instanceof Element ? event.target.closest('product-form-component') : null;
    const productId = form instanceof HTMLElement ? form.dataset.productId : '';
    if (!productId) return;

    const template = getTemplate(productId);
    if (!template) return;

    // Hold back the drawer's auto-open for this add only (cart-drawer.js reads the attribute while the event
    // is dispatched); the pop-up opens it when it closes.
    const cartDrawer = document.querySelector('cart-drawer-component[auto-open]');
    cartDrawer?.removeAttribute('auto-open');
    setTimeout(() => cartDrawer?.setAttribute('auto-open', ''));

    cartEvent.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        const inCart = (detail?.items || []).some((item) => String(item.variant_id) === template.dataset.variantId);
        if (inCart) {
          openCartDrawer();
        } else {
          showPopup(template);
        }
      })
      .catch(() => openCartDrawer());
  },
  true
);
